import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import SwitchHorizontalIcon from '@/assets/icons/switch-horizontal.svg';
import UserIcon from '@/assets/icons/user.svg';
import { Input } from '@/components/ui/input';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { getTargetConversion } from '@/utils/targets';

export function Conversion() {
  const intl = useIntl();
  const {
    hookForm,
    hiringTarget: { targetBudget: budget },
    isFetching,
    setActiveEditField,
    campaign,
  } = useEditCampaignContext();
  const isReadonly = !campaign?.permissions.is_target_metrics_editable;

  const {
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    control,
  } = hookForm;

  const [targetCpa, targetApplicants, targetHires] = watch([
    'expected_cpa',
    'target_applicants',
    'target_hires',
  ]);

  useEffect(() => {
    if (targetApplicants && targetHires && targetHires <= targetApplicants)
      clearErrors('target_hires');
  }, [targetApplicants, targetHires, clearErrors]);

  // Update expected conversion rate when target hires changes
  const handleTargetHiresChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetHires = e.target.value === '' ? null : Number(e.target.value);

    setValue('target_hires', targetHires, {
      shouldDirty: true,
    });

    setValue(
      'expected_conversion',
      getTargetConversion(budget, targetHires, targetCpa),
      { shouldDirty: true },
    );

    if (
      (!targetApplicants && targetHires) ||
      (targetApplicants && targetHires && targetHires > targetApplicants)
    )
      setError('target_hires', {
        message: intl.formatMessage({
          defaultMessage: 'Hires cannot be greater than applicants',
          description: 'Validation when target hires exceeds target applicants',
        }),
      });
    else clearErrors('target_hires');
  };

  return (
    <div className="flex items-start gap-1">
      <Input
        isNumber
        label={intl.formatMessage({
          defaultMessage: 'Predicted conversion rate',
          description:
            'Edit campaign: auto-calculated conversion from budget and targets',
        })}
        {...register('expected_conversion', {
          setValueAs: value => (value === '' ? null : Number(value)),
        })}
        min={0}
        max={100}
        placeholderLabel="%"
        placeholder={intl.formatMessage({
          defaultMessage: 'Enter target conversion rate',
          description: 'Placeholder for predicted conversion percent field',
        })}
        error={errors.expected_conversion?.message}
        isFetching={isFetching}
        onFocus={() => setActiveEditField('expected_conversion')}
        onBlur={() => setActiveEditField(null)}
        readOnly={isReadonly}
      />

      <img src={SwitchHorizontalIcon} alt="Switch" className="mt-7" />

      <Controller
        control={control}
        name="target_hires"
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ''}
            isNumber
            label={intl.formatMessage({
              defaultMessage: 'Target hires',
              description:
                'Edit campaign: desired number of hires for the period',
            })}
            onChange={e => {
              field.onChange(e);
              handleTargetHiresChange(e);
            }}
            placeholderIcon={UserIcon}
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter target hires',
              description: 'Placeholder for target hires count input',
            })}
            error={errors.target_hires?.message}
            isFetching={isFetching}
            onFocus={() => setActiveEditField('target_hires')}
            onBlur={() => setActiveEditField(null)}
            readOnly={isReadonly}
          />
        )}
      />
    </div>
  );
}
